import { MAX_PLAYERS } from './baseTable';
import Codes from '../../socket/codes';
import { API_HOST } from '../../../utils/constants';
import { ITable } from './interfaces';
import { AdditionalPlayerInfo } from './types';
import { Message } from '../../chat/message';
import Color from './color';
import { IPlayer } from '@base/features/player/model/interfaces';

export default class Player implements IPlayer {
  //   // Первичные параметры, получаемые от сервера
  pid = -1;
  name = '';
  rating = 0;
  status = 0;
  join = false;
  away = false;
  avatar = -1;
  gender = 0;
  id?: number;
  tid = -1;

  // Параметры, получаемые в качестве расширенной информации об игроке
  userId? = '';
  lastLogin? = 0;
  onlineTime? = 0;
  realLevel? = 0;
  registration? = 0;
  nGames = 0;
  nWin = 0;
  nLost = 0;
  nDraw = 0;
  avgOppRating = 0;
  maxRating? = 0;
  nMt? = 0;
  nMtWin? = 0;
  nTr? = 0;
  nTr1? = 0;
  nTr2? = 0;
  nTr3? = 0;
  ladderPos? = 0;
  playerFilter = 0;
  table?: undefined | ITable;
  country? = '';
  countryCode? = '';
  timeLag? = 0;
  lastInviteMessageTime = 0;
  lastInviteMassageTableId? = -1;
  bot? = false;
  INVITE_MESSAGE_INTERVAL = 40000; // 40 seconds between chat messages of invites (separately for players)
  /**
   * @desc Создаёт копию игрока, но чистит все ссылки на объекты
   */
  //   copy: function(player) {
  //     _.extend(this, player);
  //     this.table = undefined;
  //   },
  //
  chatMessages: Message[] = [];

  avatarUrl(): string {
    if (this.isBot() || (this.avatar > 0 && this.avatar < 32)) {
      return 'system';
    }
    if (this.userId) {
      return `${API_HOST}get_avatar.plx?size=3&uid=${this.userId}`;
    }
    return '';
  }

  getAdditionalInfo(): AdditionalPlayerInfo {
    return {
      tourneysTotal: this.nTr || 0,
      tourneysTop1: this.nTr1 || 0,
      tourneysTop2: this.nTr2 || 0,
      tourneysTop3: this.nTr3 || 0,
      miniTourneys: this.nMt || 0,
      miniTourneysWin: this.nMtWin || 0,
      ladderRanking: this.ladderPos || 0,
      totalGames: this.nGames,
      won: this.nWin,
      lost: this.nLost,
      maxRating: this.maxRating || 0,
    };
  }

  getColor() {
    const table = this.getTable();
    if (!table) return undefined;
    return table.getBlackPlayer()?.id === this.id ? Color.Black : Color.White;
  }

  //
  //   setStat(stat: Player) {
  //     Object.assign(this, stat);
  //   }
  //
  setTable(table: ITable | undefined) {
    this.tid = table ? table.tid : -1;
    this.table = table ? table : undefined;
  }

  //
  getTable(): ITable | undefined {
    return this.table;
  }

  //
  //   /**
  //    * @param table Table
  //    * @returns {boolean} True, если игрок за этим столом. В противном случае false
  //    */
  isMyTable(table: ITable | undefined): boolean {
    return !!table && table.tid !== null && this.tid === table.tid;
  }

  //
  isNotPlayer(table: ITable): boolean {
    if (!table) return true;
    if (!this.isPlayingTable(table)) return true;

    return !table.getGameState().isGameInProgress();
  }

  isPlayingTable(table: ITable): boolean {
    const seat = this.getSeat(table);
    return seat >= 0 && seat < table.getMaxPlayersCount();
  }

  isPlayingTurn(): boolean {
    return this.table?.getCurrentTurnPlayer()?.id === this.id;
  }

  getStatus() {
    const table: ITable | undefined = this.getTable();
    if (this.away) {
      return 'away';
    } else if (!table) {
      return 'idle';
    } else {
      const seat = this.getSeat(table);
      if (seat >= table.getMaxPlayersCount()) {
        return 'watching';
      } else if (table.isAllPlaying()) {
        return 'playing';
      } else {
        return 'waiting';
      }
    }
  }

  getRating(): number {
    return this.rating;
  }

  isVIP = () => {
    return this.status & Codes.PlayerFlags.VIP;
  };

  isTicketPlayer(): number {
    return this.status & Codes.PlayerFlags.MONEY;
  }

  isAdmin = () => {
    return this.status & Codes.PlayerFlags.ADM;
  };

  isTD() {
    return this.status & Codes.PlayerFlags.TD;
  }

  isTester = () => {
    return this.status & Codes.PlayerFlags.TESTER;
  };

  isBot() {
    return this.status & Codes.PlayerFlags.BOT;
  }

  isPhantom() {
    return this.status & Codes.PlayerFlags.PHANTOM;
  }

  //Максимальный лимит Points для создания игры
  getMoneyLimit(): number {
    if ((this.status & Codes.PlayerFlags.LIMIT50) === Codes.PlayerFlags.LIMIT50) {
      return 50;
    } else if ((this.status & Codes.PlayerFlags.LIMIT10) === Codes.PlayerFlags.LIMIT10) {
      return 10;
    } else if ((this.status & Codes.PlayerFlags.LIMIT25) === Codes.PlayerFlags.LIMIT25) {
      return 25;
    } else {
      return 200;
    }
  }

  /*
   * NONE(0),
   * MUTE(1),
   * IGNORE(2);
   */
  isMuted(): number {
    return this.playerFilter & Codes.PlayerFilter.MUTE;
  }

  setMuted(muted: boolean) {
    if (muted) this.playerFilter |= Codes.PlayerFilter.MUTE;
    else this.playerFilter &= ~Codes.PlayerFilter.MUTE;
  }

  isIgnored(): boolean {
    return (this.playerFilter & Codes.PlayerFilter.IGNORE) !== 0;
  }

  setIgnored(ignored: boolean) {
    if (ignored) this.playerFilter |= Codes.PlayerFilter.IGNORE;
    else this.playerFilter &= ~Codes.PlayerFilter.IGNORE;
  }

  decorateName(name: string, decorateTd: boolean) {
    if (this.isBot()) {
      return name;
    } else if (this.isTD() && decorateTd) {
      return name + '(TD)';
    } else {
      return name;
    }
  }

  //
  getName(decorate = true) {
    return this.decorateName(this.name, decorate);
  }

  getSeat(table: ITable | undefined): number {
    if (!table) {
      return -1;
    }

    for (let i = 0; i < MAX_PLAYERS; i++) {
      const player = table.getPlayerBySeat(i);
      if (player && player.getPid() === this.getPid()) {
        return i;
      }
    }
    return -1;
  }

  getPid(): number {
    if (this.id == null) throw new Error('No pid for this player!');
    return this.id;
  }

  // toString: function() {
  //   return this.name;
  // }
  //

  checkInviteMessageInterval(tableId: number): boolean {
    const now = new Date().getTime();

    // Если приглашение с другого стола менее чем за 40 секунд, то можно
    if (tableId !== this.lastInviteMassageTableId) {
      this.lastInviteMessageTime = 0;
    }

    if (now - this.lastInviteMessageTime <= this.INVITE_MESSAGE_INTERVAL) {
      return false;
    }
    this.lastInviteMassageTableId = tableId;
    this.lastInviteMessageTime = now;
    return true;
  }

  projectRating(matchResult: string, opponentRating: number): number {
    const PROVISIONAL_LIMIT = 20;
    const MAX_RATING_CHANGE = 50;

    let newRating = 0;
    const newAverageOpponentsRating =
      Math.round(this.avgOppRating * this.nGames + opponentRating) / (this.nGames + 1);

    const newNumberOfGames = this.nGames + 1;
    let newNumberOfWins = this.nWin;
    let newNumberOfLosses = this.nLost;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let newNumberOfDraws = this.nDraw;

    if (matchResult === 'win') {
      newNumberOfWins += 1;
    } else if (matchResult === 'draw') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      newNumberOfDraws += 1;
    } else if (matchResult === 'loose') {
      newNumberOfLosses += 1;
    }

    if (newNumberOfGames < PROVISIONAL_LIMIT) {
      newRating = Math.round(newAverageOpponentsRating + 400 * (newNumberOfWins - newNumberOfLosses));
      if (newRating === 0) {
        newRating = 0;
      }

      if (matchResult === 'win' && newRating < this.rating) {
        newRating = this.rating;
      } else if (matchResult === 'loose' && newRating > this.rating) {
        newRating = this.rating;
      }
    } else {
      let k: number;
      if (this.rating < 2100) {
        k = 32;
      } else if (this.rating < 2400) {
        k = 24;
      } else {
        k = 16;
      }

      const we = 1 / (Math.pow(10, (opponentRating - this.rating) / 400) + 1);
      let w;
      if (matchResult === 'win') {
        w = 1;
      } else if (matchResult === 'draw') {
        w = 0.5;
      } else if (matchResult === 'loose') {
        w = 0;
      }
      if (!w) {
        console.warn(`${matchResult} unknown`);
      } else {
        newRating = Math.round(this.rating + k * (w - we));
      }
    }

    if (newRating - this.rating > MAX_RATING_CHANGE) {
      newRating = this.rating + MAX_RATING_CHANGE;
    } else if (this.rating - newRating > MAX_RATING_CHANGE) {
      newRating = this.rating - MAX_RATING_CHANGE;
    }

    return newRating;
  }

  getGender(): number {
    return this.gender;
  }

  //messages
  addChatMessage(msg: Message): void {
    this.chatMessages.push(msg);
  }

  getChatMessages(): Message[] {
    return this.chatMessages;
  }

  static equal(player1: IPlayer, player2: IPlayer) {
    if (!player1 || !player2) return false;
    return player1 === player2 || player1.pid === player2.pid;
  }
}
